import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiChevronDown } from 'react-icons/fi';
import { apiGetLanguages } from '../../api';
import { isLanguageBrowse, languageBrowseUrl } from '../../utils/languageNavigation';

// Every Language entry uses the same browse action and the same options.
const LanguageMenu = ({ label = 'Languages', dropdown = false, onSelect }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const browsing = location.pathname === '/shop' && isLanguageBrowse(params);
  const selected = browsing ? params.get('language') || '' : '';
  const [expanded, setExpanded] = useState(!dropdown && browsing);
  const [position, setPosition] = useState({ top: 0, left: 16 });
  const [response, setResponse] = useState({
    status: 'loading',
    languages: [],
    error: '',
  });
  const [retryVersion, setRetryVersion] = useState(0);
  const loaded = useRef(false);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const panelId = useId();

  useEffect(() => {
    if (!browsing) setExpanded(false);
    else if (!dropdown) setExpanded(true);
  }, [browsing, dropdown, location.search]);

  useEffect(() => {
    if (!expanded || loaded.current) return undefined;
    let active = true;
    setResponse({ status: 'loading', languages: [], error: '' });
    apiGetLanguages()
      .then((data) => {
        if (!active) return;
        loaded.current = true;
        setResponse({ status: 'ready', languages: data.languages || [], error: '' });
      })
      .catch((error) => {
        if (!active) return;
        loaded.current = true;
        setResponse({
          status: 'error',
          languages: [],
          error: error.message || 'Could not load languages',
        });
      });
    return () => {
      active = false;
    };
  }, [expanded, retryVersion]);

  useEffect(() => {
    if (!dropdown || !expanded) return undefined;
    const dismiss = (event) => {
      if (
        !triggerRef.current?.contains(event.target) &&
        !panelRef.current?.contains(event.target)
      ) {
        setExpanded(false);
      }
    };
    const escape = (event) => {
      if (event.key === 'Escape') {
        setExpanded(false);
        triggerRef.current?.focus();
      }
    };
    const reposition = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 6,
        left: Math.max(16, Math.min(rect.left, window.innerWidth - 304)),
      });
    };
    reposition();
    document.addEventListener('pointerdown', dismiss);
    document.addEventListener('focusin', dismiss);
    document.addEventListener('keydown', escape);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      document.removeEventListener('pointerdown', dismiss);
      document.removeEventListener('focusin', dismiss);
      document.removeEventListener('keydown', escape);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [dropdown, expanded]);

  const toggle = () => {
    if (!expanded) navigate(languageBrowseUrl());
    setExpanded((current) => !current);
  };
  const choose = () => {
    if (dropdown) setExpanded(false);
    onSelect?.();
  };
  const options = response.languages;
  const itemClass = (active) =>
    `flex items-center justify-between gap-3 border-l-[3px] px-4 py-2.5 text-sm ${
      active
        ? 'border-orange-500 bg-orange-50 text-orange-600 font-semibold'
        : 'border-transparent text-black hover:bg-gray-50'
    }`;
  const panel = (
    <div
      id={panelId}
      ref={panelRef}
      role="group"
      aria-label={dropdown ? 'Navigation language choices' : 'Language choices'}
      className={
        dropdown
          ? 'fixed z-[60] w-72 max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl'
          : 'ml-4 border-l border-gray-200'
      }
      style={dropdown ? position : undefined}
    >
      <Link
        to={languageBrowseUrl()}
        onClick={choose}
        aria-current={browsing && !selected ? 'page' : undefined}
        className={itemClass(browsing && !selected)}
      >
        All Languages
      </Link>
      <div
        className="max-h-64 overflow-y-auto overscroll-contain"
        style={
          dropdown
            ? { maxHeight: `min(24rem, calc(100dvh - ${position.top + 60}px))` }
            : undefined
        }
      >
        {response.status === 'loading' ? (
          <p role="status" className="px-4 py-3 text-sm text-black">
            Loading languages...
          </p>
        ) : response.status === 'error' ? (
          <div className="px-4 py-3 text-sm text-black">
            <p role="alert">{response.error}</p>
            <button
              type="button"
              className="mt-2 text-orange-600"
              onClick={() => {
                loaded.current = false;
                setRetryVersion((value) => value + 1);
              }}
            >
              Try again
            </button>
          </div>
        ) : options.length ? (
          <ul className="list-none">
            {options.map(({ name, count }) => (
              <li key={name}>
                <Link
                  to={languageBrowseUrl(name)}
                  onClick={choose}
                  aria-current={selected === name ? 'page' : undefined}
                  className={itemClass(selected === name)}
                >
                  <span>{name}</span>
                  <span className="shrink-0 text-xs">({count})</span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-4 py-3 text-sm text-black">No languages available.</p>
        )}
      </div>
    </div>
  );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={toggle}
        className={
          dropdown
            ? `flex items-center gap-1 whitespace-nowrap text-sm font-medium ${browsing || expanded ? 'text-amber-400' : 'text-white hover:text-amber-400'}`
            : `${itemClass(browsing)} w-full`
        }
      >
        {label}
        <FiChevronDown
          aria-hidden="true"
          className={`shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
        />
      </button>
      {expanded && (dropdown ? createPortal(panel, document.body) : panel)}
    </>
  );
};

export default LanguageMenu;
